import { useState, useCallback, useRef } from "react";
import { UIMsg, Conversation } from "../types";
import { useAppSettings } from "../../../components/providers/AppSettingsProvider";
import { loadAuthUser } from "../../../services/auth";
import { getUserStorageKey, migrateLegacyData } from "../../../utils/storage";
import { toCanonicalPersonaId } from "../../../utils/persona";

const BASE_STORAGE_KEY = 't2s_conversations';

const normalizeConversationId = (id?: string | null): string => (id || '').trim();

export function useConversation() {
    const { t } = useAppSettings();
    
    // Get current user ID to key storage
    const user = loadAuthUser();
    const userId = user?.id;
    
    // derived storage key
    const storageKey = getUserStorageKey(BASE_STORAGE_KEY, userId);

    // Attempt migration on mount (or when userId changes)
    // We prefer doing this before initializing state, but state init runs first.
    // However, since we read from LS in state init, we might miss it on first render?
    // Actually, migration is synchronous. But we can't run side-effects in render.
    // Better strategy: 
    // 1. Run migration logic in a "useMemo" or similar synchronous block if possible? 
    //    No, side effects discouraged.
    // 2. Just Initialize state with the "Best Guess".
    //    If migration happens inside useEffect, we need to reload state.
    
    // Let's do a simple lazy init that checks migration *if* we can.
    // But migration modifies LS. That's a side effect.
    
    // Alternative: Just run migration right here in body? 
    // It's a bit dirty but ensures data is ready before useState reads it.
    // Given React 18 strict mode, it might run twice. That's fine (2nd run does nothing).
    if (userId) {
       migrateLegacyData(BASE_STORAGE_KEY, userId);
    }

    // --- Helpers ---
    const calculateWordCount = useCallback((messages: UIMsg[]): number => {
        return messages.reduce((total, msg) => total + (msg.text?.length || 0), 0);
    }, []);

    const generateTitle = useCallback((msgs: UIMsg[]): string => {
        const firstUserMsg = msgs.find(m => m.role === 'user')?.text;
        if (firstUserMsg) return firstUserMsg;

        const firstAiMsg = msgs.find(m => m.role === 'assistant')?.text;
        if (firstAiMsg) return firstAiMsg;

        return '';
    }, []);

    // --- States ---

    // All historical conversations
    const [conversations, setConversations] = useState<Conversation[]>(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return [];
            return parsed.map((conv: Conversation) => ({
                ...conv,
                id: normalizeConversationId(conv?.id),
                personaId: toCanonicalPersonaId(conv?.personaId),
            }));
        } catch { return []; }
    });

    // Current active session messages
    const [currentMessages, setCurrentMessages] = useState<UIMsg[]>([]);
    const currentMessagesRef = useRef<UIMsg[]>(currentMessages);

    const updateCurrentMessages: React.Dispatch<React.SetStateAction<UIMsg[]>> = useCallback((value) => {
        setCurrentMessages(prev => {
            const next = typeof value === 'function' ? (value as any)(prev) : value;
            currentMessagesRef.current = next;
            return next;
        });
    }, []);

    // ID of the conversation we are currently "continuing" (if any)
    const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

    // Completed first conversation flag
    const [hasCompletedFirstConversation, setHasCompletedFirstConversation] = useState(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return false;
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) && parsed.length > 0;
        } catch { return false; }
    });

    // UI State
    const [conversationSelected, setConversationSelected] = useState(false);
    const [showTranscript, setShowTranscript] = useState(false);

    // --- Actions ---

    const loadConversations = useCallback(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    setConversations(parsed.map((conv: Conversation) => ({
                        ...conv,
                        id: normalizeConversationId(conv?.id),
                        personaId: toCanonicalPersonaId(conv?.personaId),
                    })));
                }
            }
        } catch (e) { console.error(e); }
    }, []);

    const startNewConversation = useCallback(() => {
        setActiveConversationId(null);
        updateCurrentMessages([]);
        setShowTranscript(false);
        setConversationSelected(true);
    }, []);

    const saveConversation = useCallback((
        sessionDuration: number = 0,
        language: string = 'en-US',
        personaId?: string,
        gender?: string,
        manualMessages?: UIMsg[],
        manualActiveId?: string | null,
    ) => {
        const msgs = manualMessages || currentMessagesRef.current;
        if (msgs.length === 0) return;

        let targetId = manualActiveId !== undefined ? manualActiveId : activeConversationId;
        targetId = normalizeConversationId(targetId);
        let isNew = false;
        const canonicalPersonaId = personaId ? toCanonicalPersonaId(personaId) : undefined;

        // If no ID, generate one now to ensure we can set it to state
        if (!targetId) {
            const now = Date.now();
            targetId = `conv_${now}`;
            isNew = true;
        }

        setConversations(prev => {
            let updated = [...prev];
            const now = Date.now(); // Update timestamp for modification

            if (!isNew) {
                // Update existing
                let found = false;
                updated = updated.map(conv => {
                    if (conv.id === targetId) {
                        found = true;
                        const wordCount = calculateWordCount(msgs);
                        const totalDuration = (conv.duration || 0) + sessionDuration;

                        // Update title if it's currently generic or empty, or if we now have a user message
                        const rawTitle = generateTitle(msgs);
                        const displayTitle = rawTitle.length > 30 ? rawTitle.slice(0, 30) + '...' : rawTitle;
                        const isGeneric = !conv.title || conv.title === t('whisper.newConversation') || conv.title === 'New Conversation';
                        const hasUserMsg = msgs.some(m => m.role === 'user');

                        return {
                            ...conv,
                            messages: msgs,
                            updatedAt: now,
                            title: (isGeneric || hasUserMsg) ? (displayTitle || conv.title) : conv.title,
                            // ✅ 只在明确传入时才覆盖，否则保留对话自己记录的值
                            // 防止 handleExit 用 stale 的全局 selectedPersonaId 覆盖历史对话的正确 persona
                            personaId: canonicalPersonaId || toCanonicalPersonaId(conv.personaId),
                            gender: gender || conv.gender,
                            wordCount,
                            duration: totalDuration,
                        };
                    }
                    return conv;
                });

                // UPSERT: If we have a targetId (e.g. from backend) but didn't find it in local list,
                // we must CREATE it instead of just returning the unchanged list.
                if (!found && targetId) {
                    // Create new with specific ID
                    const rawTitle = generateTitle(msgs);
                    const title = rawTitle.length > 30 ? rawTitle.slice(0, 30) + '...' : rawTitle;
                    const wordCount = calculateWordCount(msgs);

                    const newConv: Conversation = {
                        id: targetId,
                        messages: msgs,
                        createdAt: now,
                        updatedAt: now,
                        title: title || t('whisper.newConversation') || 'New Conversation',
                        language: language,
                        personaId: canonicalPersonaId || 'listener',
                        gender: gender || 'female',
                        wordCount,
                        duration: sessionDuration,
                    };
                    updated = [newConv, ...updated];
                }
            } else {
                // Create new
                // Use the ID generated above
                // Note: using 'now' from outer scope for ID consistency if we wanted, 
                // but we already baked it into targetId.
                // We need createdAt. We can parse it from ID or just use `now`? 
                // Ideally `createdAt` matches the ID timestamp.

                // Let's re-parse or just rely on 'now' being close enough if we use a new 'now'?
                // Better: keep 'createdAt' valid. 
                // parsing `conv_${timestamp}` back might be safest or just use the variable if we captured it.
                // Since we can't easily capture the *exact* timestamp used in ID inside this callback without passing it,
                // let's assume we use the timestamp from the ID or just a fresh one.
                // Actually, let's keep it simple: 
                // We generated `targetId` with `Date.now()`.
                // Let's reuse that `createdAt` if possible? 
                // We can't pass extra args to setConversations callback easily.

                // Workaround: Parse timestamp from targetId
                const createdAt = parseInt(targetId!.split('_')[1]);
                const rawTitle = generateTitle(msgs);
                const title = rawTitle.length > 30 ? rawTitle.slice(0, 30) + '...' : rawTitle;
                const wordCount = calculateWordCount(msgs);

                const newConv: Conversation = {
                    id: targetId!,
                    messages: msgs,
                    createdAt: createdAt,
                    updatedAt: now,
                    title: title || t('whisper.newConversation') || 'New Conversation',
                    language: language,
                    personaId: canonicalPersonaId || 'listener',
                    gender: gender || 'female',
                    wordCount,
                    duration: sessionDuration,
                };

                updated = [newConv, ...updated];
            }

            try {
                console.log('[Debug] saveConversation - targetId:', targetId, 'isNew:', isNew);
                console.log('[Debug] Saving updated conversations to LS:', updated);
                localStorage.setItem(storageKey, JSON.stringify(updated));
                if (updated.length > 0) {
                    setHasCompletedFirstConversation(true);
                }
            } catch (e) {
                console.error("Failed to save conversations", e);
            }

            return updated;
        });

        // CRITICAL: Update active ID if we created a new one
        if (isNew && targetId) {
            setActiveConversationId(targetId);
        }

    }, [currentMessages, activeConversationId, t, calculateWordCount]);

    const selectConversation = useCallback((id: string): { personaId?: string; gender?: string } | null => {
        const conv = conversations.find(c => c.id === id);
        if (conv) {
            setActiveConversationId(conv.id);
            updateCurrentMessages(conv.messages);
            setConversationSelected(true);
            setShowTranscript(true);
            // 返回对话记录的 persona/gender，供调用方恢复 UI 状态
            return { personaId: conv.personaId, gender: conv.gender };
        }
        return null;
    }, [conversations]);

    const deleteConversation = useCallback((id: string) => {
        setConversations(prev => {
            const updated = prev.filter(c => c.id !== id);
            localStorage.setItem(storageKey, JSON.stringify(updated));
            return updated;
        });
        if (activeConversationId === id) {
            setConversationSelected(false);
            setActiveConversationId(null);
        }
    }, [activeConversationId, storageKey]);

    const migrateConversationId = useCallback((oldId: string, newId: string) => {
        const oldNormalizedId = normalizeConversationId(oldId);
        const newNormalizedId = normalizeConversationId(newId);
        if (!oldNormalizedId || !newNormalizedId || oldNormalizedId === newNormalizedId) return;
        
        setConversations(prev => {
            const updated = prev.map(conv => {
                if (normalizeConversationId(conv.id) === oldNormalizedId) {
                    return { ...conv, id: newNormalizedId };
                }
                return conv;
            });
            
            try {
                localStorage.setItem(storageKey, JSON.stringify(updated));
                console.log(`[useConversation] Migrated conversation ID from ${oldNormalizedId} to ${newNormalizedId}`);
            } catch (e) {
                console.error("Failed to save migrated conversation", e);
            }
            
            return updated;
        });
        
        // Update active ID if it matches the one being migrated
        setActiveConversationId(prev => normalizeConversationId(prev) === oldNormalizedId ? newNormalizedId : prev);
    }, [storageKey]);

    const restoreConversationsFromCloud = useCallback(async (limit: number = 5) => {
        try {
            const { fetchRecentConversations } = await import("../../../services/journalService");
            const cloudConvs = await fetchRecentConversations(limit);
            if (cloudConvs.length === 0) return [];

            setConversations(prev => {
                const localIds = new Set(prev.map(c => c.id));
                const filteredCloud = cloudConvs.filter(c => !localIds.has(c.id));
                
                if (filteredCloud.length === 0) return prev;

                const updated = [...filteredCloud, ...prev];
                // Sort by createdAt desc
                updated.sort((a, b) => b.createdAt - a.createdAt);
                
                localStorage.setItem(storageKey, JSON.stringify(updated));
                if (updated.length > 0) {
                    setHasCompletedFirstConversation(true);
                }
                return updated;
            });
            
            return cloudConvs;
        } catch (e) {
            console.error('[useConversation] restoreConversationsFromCloud failed:', e);
            return [];
        }
    }, [storageKey]);

    return {
        conversations,
        currentMessages,
        setCurrentMessages: updateCurrentMessages,
        updateCurrentMessages, // Alias
        activeConversationId,
        setActiveConversationId,
        hasCompletedFirstConversation,
        conversationSelected,
        setConversationSelected,
        showTranscript,
        setShowTranscript,

        loadConversations,
        startNewConversation,
        saveConversation,
        selectConversation,
        deleteConversation,
        migrateConversationId,
        calculateWordCount,
        restoreConversationsFromCloud
    };
}
